import {
  ChangeDetectionStrategy,
  Component,
  inject,
  input,
  output,
} from '@angular/core';
import { AppSvgIconComponent, OverlayService } from '@vallift/ngl';
import {
  CollectionPickerOverlayComponent,
  type CollectionPickerResult,
} from '../collection-picker-overlay/collection-picker-overlay';
import {
  CollectionColumnPickerOverlayComponent,
  type CollectionColumnPickerResult,
  type CollectionColumnSelectionMode,
} from '../collection-column-picker-overlay/collection-column-picker-overlay';

/** Collection + chosen display column(s) after pickers complete. */
export interface LinkedCollectionResult extends CollectionPickerResult {
  displayFieldKey: string;
  displayFieldLabel?: string;
  secondaryDisplayFieldKey?: string;
  secondaryDisplayFieldLabel?: string;
  /** Optional collection item id for the field default. */
  defaultItemId?: string;
}

@Component({
  selector: 'app-link-collection-button',
  standalone: true,
  imports: [AppSvgIconComponent],
  templateUrl: './link-collection-button.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LinkCollectionButtonComponent {
  private readonly overlayService = inject(OverlayService);

  readonly label = input('Link collection');
  readonly selectionMode = input<CollectionColumnSelectionMode>('single');
  readonly collectionSelected = output<LinkedCollectionResult>();

  openPicker(): void {
    const dialogRef = this.overlayService.openModal(
      CollectionPickerOverlayComponent,
      {},
    );
    dialogRef.closed.subscribe(
      (result: CollectionPickerResult | null | undefined) => {
        if (!result) {
          return;
        }
        this.openColumnPicker(result);
      },
    );
  }

  private openColumnPicker(collection: CollectionPickerResult): void {
    const mode = this.selectionMode();
    const columnRef = this.overlayService.openModal(
      CollectionColumnPickerOverlayComponent,
      {
        fitContent: true,
        panelClass: [
          mode === 'primarySecondary' ? 'w-[24rem]' : 'w-[22rem]',
          'max-w-[calc(100%-2rem)]',
        ],
        data: {
          collectionId: collection.id,
          collectionName: collection.name,
          selectionMode: mode,
        },
      },
    );
    columnRef.closed.subscribe(
      (column: CollectionColumnPickerResult | null | undefined) => {
        if (!column?.displayFieldKey) {
          return;
        }
        if (
          mode === 'primarySecondary' &&
          !column.secondaryDisplayFieldKey?.trim()
        ) {
          return;
        }
        this.collectionSelected.emit({
          ...collection,
          displayFieldKey: column.displayFieldKey,
          displayFieldLabel: column.displayFieldLabel,
          ...(column.secondaryDisplayFieldKey
            ? {
                secondaryDisplayFieldKey: column.secondaryDisplayFieldKey,
                secondaryDisplayFieldLabel: column.secondaryDisplayFieldLabel,
              }
            : {}),
          ...(column.defaultItemId
            ? { defaultItemId: column.defaultItemId }
            : {}),
        });
      },
    );
  }
}
